import { ArrowLeft, ExternalLink, FileText, Tag } from 'lucide-react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import ROUTES from '@/ROUTES'

import { IPublicResource } from '@/MODELS/publicResource.model'

import S3Service from '@/services/s3.service'
import { useAuth } from '@/services/authentication.service'
import { useResource } from '@/services/resource.service'

import Empty from '@/components/Empty'
import Loading from '@/components/Loading'
import { SectionHeader } from '@/components/SectionHeader'
import S3ModalViewer from '@/components/s3/S3ModalViewer'

export default function SingleResourcePage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const [resource, , ResourceResource] = useResource<IPublicResource>(`${ROUTES.app.publicResource}/${id}`)
    const { user } = useAuth()

    const [showFileViewer, setShowFileViewer] = useState(false)

    if (ResourceResource.isLoading) {
        return (
            <div className='flex items-center justify-center min-h-screen'>
                <Loading isLoading={true} text='Loading resource...' />
            </div>
        )
    }

    if (resource === null) {
        return (
            <div className='flex items-center justify-center min-h-screen'>
                <Empty text='No resource found' />
            </div>
        )
    }

    const isFileResource = resource.resourceType === 'file' || (resource.s3ObjectId && !resource.link)
    const displayBehavior = resource.displayBehavior || 'new_tab'

    function handleFileClick() {
        if (!resource?.s3ObjectId) return

        if (displayBehavior === 'modal') {
            setShowFileViewer(true)
        } else {
            S3Service.getFileUrlFromS3(resource.s3ObjectId).then(({ signedUrl }) => {
                if (displayBehavior === 'new_window') {
                    window.open(signedUrl, '_blank', 'width=900,height=700,scrollbars=yes,resizable=yes')
                } else {
                    window.open(signedUrl, '_blank')
                }
            })
        }
    }

    function handleLinkClick(e: React.MouseEvent) {
        if (!resource?.link) return

        if (displayBehavior === 'new_window') {
            e.preventDefault()
            window.open(resource.link, '_blank', 'width=900,height=700,scrollbars=yes,resizable=yes')
        }
        // 'new_tab' uses default <a target="_blank"> behavior
    }

    return (
        <div className='flex-1 w-full mx-auto max-w-7xl'>
            <SectionHeader title='Resource' subtitle={resource.category || 'Explore this resource'} />

            <div className='px-4 pt-4 sm:max-w-6xl sm:mx-auto'>
                <span
                    onClick={() => navigate(-1)}
                    className='inline-flex items-center space-x-2 transition-colors cursor-pointer text-cyan-600 hover:text-cyan-700'
                >
                    <ArrowLeft className='w-5 h-5 shrink-0' />
                    <span className='font-medium'>Back to Resources</span>
                </span>
            </div>

            <div className='max-w-4xl px-6 py-4 mx-auto'>
                <div className='p-6 mb-4 bg-white border border-gray-100 rounded-2xl sm:p-8'>
                    <div className='flex items-start mb-6 space-x-4'>
                        <div className='flex-1'>
                            <h1 className='mb-2 text-lg font-bold text-gray-900 sm:text-2xl'>{resource.title}</h1>
                            <p className='text-base text-gray-600 sm:text-lg'>
                                {resource.description || 'No description available.'}
                            </p>
                        </div>
                    </div>

                    <div className='grid grid-cols-1 gap-6 mx-3 mb-6 md:grid-cols-2'>
                        <div>
                            <h3 className='flex items-center mb-3 space-x-2 font-semibold text-gray-900'>
                                <Tag className='w-4 h-4' />
                                <span>Category</span>
                            </h3>
                            {resource.category ? (
                                <span className='inline-block px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 rounded-full'>
                                    {resource.category}
                                </span>
                            ) : (
                                <p className='text-sm text-gray-500'>No category assigned</p>
                            )}
                        </div>

                    </div>

                    {/* File Resource */}
                    {isFileResource && resource.s3ObjectId && (
                        <>
                            <div
                                onClick={handleFileClick}
                                className='p-6 mb-6 cursor-pointer bg-brand-100/50 rounded-xl hover:bg-brand-100 transition-colors'
                            >
                                <div className='flex items-center justify-between'>
                                    <div className='flex items-center space-x-3'>
                                        <FileText className='w-5 h-5 text-cyan-600' />
                                        <span className='font-medium text-cyan-900'>View File</span>
                                    </div>
                                </div>
                            </div>
                            {displayBehavior === 'modal' && (
                                <S3ModalViewer
                                    s3ObjectId={resource.s3ObjectId}
                                    open={showFileViewer}
                                    onClose={() => setShowFileViewer(false)}
                                />
                            )}
                        </>
                    )}

                    {/* Link Resource */}
                    {!isFileResource && resource.link && (
                        <a
                            href={resource.link}
                            target='_blank'
                            rel='noopener noreferrer'
                            onClick={handleLinkClick}
                        >
                            <div className='p-6 mb-6 bg-brand-100/50 rounded-xl hover:bg-brand-100 transition-colors'>
                                <div className='flex items-center justify-between'>
                                    <div className='flex items-center space-x-3'>
                                        <ExternalLink className='w-5 h-5 text-cyan-600' />
                                        <span className='font-medium text-cyan-900'>Learn More</span>
                                    </div>
                                </div>
                            </div>
                        </a>
                    )}
                </div>
            </div>
        </div>
    )
}
